import { useState, useRef, useCallback, useEffect } from 'react';
import { FiShield, FiMail, FiMessageSquare, FiRefreshCw } from 'react-icons/fi';

import { VerificationLogoutButton } from '@/shared/components/VerificationLogoutButton';

interface DeviceAuthOverlayProps {
  channelsAvailable: { email: boolean; whatsapp: boolean };
  maskedEmail?: string;
  maskedPhone?: string;
  onSelectChannel: (channel: 'email' | 'whatsapp') => Promise<void>;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  error?: string;
  isBusy?: boolean;
  onLogout: () => void;
}

/**
 * DeviceAuthOverlay — Phase D.
 *
 * Modal bloqueante de autorização 2FA do dispositivo.
 * Fluxo:
 *   1. Passo 1 — usuário escolhe canal (email ou whatsapp). **Pulado na primeira
 *      abertura**: o canal é auto-selecionado uma vez, preferindo email quando
 *      disponível (mais confiável que o WhatsApp — ver incidente 2026-07-16).
 *   2. Passo 2 — usuário digita o código de 6 dígitos.
 *
 * "Mudar canal" volta ao Passo 1 e, a partir daí, a escolha é sempre do usuário:
 * a auto-seleção não se repete (hasAutoSelectedRef).
 */
export function DeviceAuthOverlay({
  channelsAvailable,
  maskedEmail,
  maskedPhone,
  onSelectChannel,
  onVerify,
  onResend,
  error,
  isBusy,
  onLogout,
}: DeviceAuthOverlayProps) {
  const [selectedChannel, setSelectedChannel] = useState<'email' | 'whatsapp' | null>(null);
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(0);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Guarda de "auto-seleção já aconteceu". ESSENCIAL, não defensividade:
  // `selectedChannel` está nas deps do effect abaixo, e tanto "Mudar canal" quanto o
  // rollback de erro do handleChooseChannel fazem setSelectedChannel(null). Sem este
  // ref, cada reset re-dispararia o effect e re-selecionaria email na hora — o botão
  // "Mudar canal" viraria no-op, o WhatsApp ficaria inalcançável, e o usuário ficaria
  // preso justamente no caso em que o EMAIL é que está falhando.
  const hasAutoSelectedRef = useRef(false);

  // Auto-seleciona o canal UMA ÚNICA VEZ. Email é o DEFAULT quando ambos estão
  // disponíveis: o WhatsApp depende de um canal externo que pode cair (incidente
  // 2026-07-16, usuários travados no login), enquanto o email é o mais confiável.
  // Depois da primeira vez, a escolha é sempre do usuário via Passo 1.
  useEffect(() => {
    if (selectedChannel || hasAutoSelectedRef.current) return;
    const available = Object.entries(channelsAvailable).filter(([, v]) => v).map(([k]) => k as 'email' | 'whatsapp');
    if (available.length === 0) return;
    const preferred: 'email' | 'whatsapp' | undefined = available.includes('email') ? 'email' : available[0];
    if (preferred) {
      hasAutoSelectedRef.current = true;
      setSelectedChannel(preferred);
      // Rollback no erro, igual ao handleChooseChannel: volta pro Passo 1 pro usuário
      // escolher outro canal, em vez de ficar parado no Passo 2 esperando um código
      // que nunca vem. Com o ref acima, esse reset não re-dispara a auto-seleção.
      onSelectChannel(preferred).catch(() => { setSelectedChannel(null); });
    }
  }, [channelsAvailable, selectedChannel, onSelectChannel]);

  useEffect(() => {
    if (!selectedChannel) return;
    setCountdown(60);
  }, [selectedChannel]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleChooseChannel = async (channel: 'email' | 'whatsapp') => {
    setSelectedChannel(channel);
    try {
      await onSelectChannel(channel);
    } catch {
      setSelectedChannel(null);
    }
  };

  const handleChange = useCallback((index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5) {
      const code = newDigits.join('');
      if (code.length === 6) {
        onVerify(code);
      }
    }
  }, [digits, onVerify]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }, [digits]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').trim().slice(0, 6);
    if (!/^\d+$/.test(text)) return;
    const newDigits = text.split('').concat(Array(6 - text.length).fill(''));
    setDigits(newDigits);
    if (text.length === 6) {
      onVerify(text);
    }
  }, [onVerify]);

  const handleResend = useCallback(async () => {
    if (countdown > 0) return;
    setResending(true);
    try {
      await onResend();
      setCountdown(60);
      setDigits(['', '', '', '', '', '']);
    } finally {
      setResending(false);
    }
  }, [countdown, onResend]);

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
  };

  const cardStyle: React.CSSProperties = {
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '32px 28px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  };

  // Passo 1: escolha de canal
  if (!selectedChannel) {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <VerificationLogoutButton onLogout={onLogout} disabled={isBusy} />
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#f3e8ff', marginBottom: '16px' }}>
              <FiShield style={{ width: '28px', height: '28px', color: '#8b5cf6' }} />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#1e293b', margin: 0, marginBottom: '8px' }}>
              Autorize este dispositivo
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
              Por segurança, precisamos confirmar que é você usando este dispositivo. Escolha como receber o código.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {channelsAvailable.email && (
              <button
                type="button"
                onClick={() => handleChooseChannel('email')}
                disabled={isBusy}
                data-testid="device-auth-channel-email"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#1e293b',
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(0, 0, 0, 0.10)',
                  borderRadius: '8px',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  opacity: isBusy ? 0.6 : 1,
                  textAlign: 'left',
                }}
              >
                <FiMail style={{ width: '18px', height: '18px', color: '#8b5cf6' }} />
                <span style={{ flex: 1 }}>
                  Enviar código por email
                  {maskedEmail && <span style={{ display: 'block', fontSize: '12px', color: '#94a3b8' }}>{maskedEmail}</span>}
                </span>
              </button>
            )}
            {channelsAvailable.whatsapp && (
              <button
                type="button"
                onClick={() => handleChooseChannel('whatsapp')}
                disabled={isBusy}
                data-testid="device-auth-channel-whatsapp"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#1e293b',
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(0, 0, 0, 0.10)',
                  borderRadius: '8px',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  opacity: isBusy ? 0.6 : 1,
                  textAlign: 'left',
                }}
              >
                <FiMessageSquare style={{ width: '18px', height: '18px', color: '#10b981' }} />
                <span style={{ flex: 1 }}>
                  Enviar código por WhatsApp
                  {maskedPhone && <span style={{ display: 'block', fontSize: '12px', color: '#94a3b8' }}>{maskedPhone}</span>}
                </span>
              </button>
            )}
          </div>

          {error && (
            <p style={{ fontSize: '13px', color: '#ef4444', margin: '16px 0 0', padding: '8px 12px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Passo 2: entrada do código
  const target = selectedChannel === 'email' ? maskedEmail : maskedPhone;
  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <VerificationLogoutButton onLogout={onLogout} disabled={isBusy} />
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#f3e8ff', marginBottom: '16px' }}>
            <FiShield style={{ width: '28px', height: '28px', color: '#8b5cf6' }} />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#1e293b', margin: 0, marginBottom: '8px' }}>
            Digite o código de 6 dígitos
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
            Enviamos para <strong>{target}</strong>. O código expira em 10 minutos.
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px' }} onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={isBusy}
              data-testid={`device-auth-digit-${index}`}
              style={{
                width: '44px',
                height: '52px',
                fontSize: '22px',
                fontWeight: 600,
                textAlign: 'center',
                borderRadius: '8px',
                border: '1px solid rgba(0, 0, 0, 0.10)',
                backgroundColor: '#ffffff',
                color: '#1e293b',
                outline: 'none',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#8b5cf6')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.10)')}
            />
          ))}
        </div>

        {error && (
          <p style={{ fontSize: '13px', color: '#ef4444', margin: '0 0 16px', padding: '8px 12px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
            {error}
          </p>
        )}

        {isBusy && (
          <p style={{ fontSize: '13px', color: '#8b5cf6', textAlign: 'center', margin: '0 0 16px' }}>Verificando...</p>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
          <button
            type="button"
            onClick={() => setSelectedChannel(null)}
            disabled={isBusy}
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              cursor: isBusy ? 'not-allowed' : 'pointer',
              padding: 0,
              fontSize: '13px',
              textDecoration: 'underline',
            }}
          >
            Mudar canal
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={countdown > 0 || resending || isBusy}
            data-testid="device-auth-resend"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              background: 'none',
              border: 'none',
              color: countdown > 0 ? '#94a3b8' : '#8b5cf6',
              cursor: countdown > 0 || resending || isBusy ? 'not-allowed' : 'pointer',
              padding: 0,
              fontSize: '13px',
            }}
          >
            <FiRefreshCw style={{ width: '12px', height: '12px' }} />
            {countdown > 0 ? `Reenviar em ${countdown}s` : resending ? 'Reenviando...' : 'Reenviar código'}
          </button>
        </div>
      </div>
    </div>
  );
}
